import React, { useState } from "react";
import FormInput from "Common/Custom/FormInput";
import FormSelect from "Common/Custom/FormSelect";
import FormMultiSelect from "Common/Custom/FormMultiSelect";
import { getSelectOptions } from "utils/optionHelper";
import Switch from "Common/Custom/Switch";

const HallRoomDetailsList = ({
  halls,
  handleHallChange,
  formdata,
  optionConfig,
  errors,
  addHall,
  removeHall,
  values,
  foodPackages,
  foodPackageDetails,
  hallRoomTypes,
  isHallSectionEnabled = false,
  isEditMode = false
}) => {

  const [imageModal, setImageModal] = useState({
    isOpen: false,
    imageUrl: '',
    foodName: '',
    foodDescription: ''
  });

  const calculateHours = (start, end) => {
    if (!start || !end) return 0;
    const startTime = new Date(start);
    const endTime = new Date(end);
    if (isNaN(startTime) || isNaN(endTime)) return 0;
    const diffMs = endTime - startTime;
    return diffMs > 0 ? (diffMs / (1000 * 60 * 60)) : 0;
  };

  const handleImageClick = (food) => {
    setImageModal({
      isOpen: true,
      imageUrl: food.image_url,
      foodName: food.food_name,
      foodDescription: food.description
    });
  };

  const closeImageModal = () => {
    setImageModal({
      isOpen: false,
      imageUrl: '',
      foodName: '',
      foodDescription: ''
    });
  };

  const RoomDetails = formdata?.RoomDetails || [];

  return (
    <div className="hall-booking-container">
      <div className="col-12 mt-4">
        <div className="card hall-room-details-card">
          <div className="card-header fw-semibold">Hall Room Details</div>
          <div className="card-body p-0">
            <div className="row g-3">
              <div className="col-12">
                {halls.map((hall, idx) => (
                  <React.Fragment key={idx}>
                    {halls.length > 1 && (
                      <div className="border border-dashed border-bottom-0 py-1 text-muted fw-bold text-center">
                        <span className="opacity-25 fs-3">Hall {idx + 1}</span>
                      </div>
                    )}

                    <div className="table-responsive">
                      <table className="table mb-0 border">
                        <thead>
                          <tr>
                            <th className="p-0">
                              <div className="px-3 py-2 fw-semibold">Hall Info</div>
                            </th>
                            <th className="p-0"></th>
                            <th className="p-0">
                              <div className="border-start px-3 py-2 fw-semibold">Action</div>
                            </th>
                          </tr>
                        </thead>

                        <tbody>
                          <tr>
                            <td className="align-top border-end border-bottom-0" colSpan={2}>
                              <div className="row g-3 p-3">
                                {RoomDetails.map((field) => {
                                  if (field.name === "hallroom_type_id") {
                                    return (
                                      <FormSelect
                                        key={field.name}
                                        field={{ ...field, disabled: !isHallSectionEnabled }}
                                        value={hall.hallroom_type_id}
                                        options={getSelectOptions(field.name, optionConfig)}
                                        handleChange={(e) => handleHallChange(idx, "hallroom_type_id", e.target.value)}
                                        error={errors?.halls?.[idx]?.hallroom_type_id}
                                      />
                                    );
                                  } else if (field.name === "seatplan_id") {
                                    const selectedHallType = hallRoomTypes?.find(t => String(t.id) === String(hall.hallroom_type_id));
                                    const filteredSeatPlans = selectedHallType
                                      ? hallRoomTypes
                                        ?.filter(t => t.hallroom_type === selectedHallType.hallroom_type)
                                        .map(t => ({ id: t.seatplan_id, plan_name: t.plan_name }))
                                        .filter((v, i, a) => a.findIndex(t => String(t.id) === String(v.id)) === i)
                                      : [];

                                    return (
                                      <FormSelect
                                        key={field.name}
                                        field={{ ...field, disabled: !isHallSectionEnabled }}
                                        value={hall.seatplan_id}
                                        options={filteredSeatPlans.map(sp => ({
                                          value: sp.id,
                                          label: sp.plan_name
                                        }))}
                                        handleChange={(e) => handleHallChange(idx, "seatplan_id", e.target.value)}
                                        error={errors?.halls?.[idx]?.seatplan_id}
                                      />
                                    );
                                  } else if (field.name === "capacity") {
                                    return (
                                      <FormInput
                                        key={field.name}
                                        field={{
                                          ...field,
                                          disabled: !isHallSectionEnabled,
                                          label: `${field.label} max ${hallRoomTypes?.find(t => String(t.id) === String(hall.hallroom_type_id))?.capacity || ''}`
                                        }}
                                        value={hall[field.name]}
                                        handleChange={(e) => handleHallChange(idx, field.name, e.target.value)}
                                        error={errors?.halls?.[idx]?.[field.name]}
                                      />
                                    );
                                  }
                                  return null;
                                })}
                              </div>
                            </td>

                            <td className="align-bottom text-center px-2 py-3" style={{ width: "1%" }}>
                              <button
                                type="button"
                                onClick={addHall}
                                className="btn btn-sm btn-primary"
                                title="Add Hall"
                                disabled={(idx !== halls.length - 1) || !isHallSectionEnabled}
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" width={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="svg-stroke">
                                  <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                  <path d="M12 5l0 14" />
                                  <path d="M5 12l14 0" />
                                </svg>
                              </button>
                            </td>
                          </tr>

                          <tr>
                            <td colSpan="2" className="p-0">
                              <div className="row g-0">
                                <div className="col-lg-7">
                                  <div className="d-flex align-items-center border-top border-end px-3 py-2 justify-content-between bg-light bg-opacity-10 border-bottom">
                                    <div className="fw-semibold">Customer Info</div>
                                  </div>
                                  <div className="table-responsive border-end">
                                    <table className="table table-sm mb-0">
                                      <thead className="bg-light">
                                        <tr>
                                          <th width="60" className="text-center">ID</th>
                                          <th>Name</th>
                                          <th>Mobile No</th>
                                          <th width="80" className="text-center">Action</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {values.customer_id ? (
                                          <tr>
                                            <td className="text-center align-middle border-0">{values.customer_id}</td>
                                            <td className="align-middle border-0">{values.customer_name || 'N/A'}</td>
                                            <td className="align-middle border-0">{values.customer_phone || 'N/A'}</td>
                                            <td className="text-center align-middle border-0">
                                              <span className="badge bg-secondary opacity-50">Locked</span>
                                            </td>
                                          </tr>
                                        ) : (
                                          <tr><td colSpan="4" className="text-center py-4 text-muted small border-0">Select Customer Details</td></tr>
                                        )}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>

                                <div className="col-lg-5">
                                  <div className="d-flex align-items-center border-top border-bottom px-3 py-2 bg-light bg-opacity-10">
                                    <div className="fw-semibold">Rent Info</div>
                                  </div>
                                  <div className="row g-2 small p-3">
                                    {RoomDetails.map(field => field.name === "hourly_rate" && (
                                      <FormInput
                                        key={field.name}
                                        field={{
                                          ...field,
                                          disabled: !isHallSectionEnabled,
                                          label: `Hall Rent Total (${hall.per_hour_rate || 0} × ${calculateHours(values.start_date, values.end_date).toFixed(2)} hrs)`
                                        }}
                                        value={hall.hourly_rate}
                                        handleChange={(e) => handleHallChange(idx, "hourly_rate", e.target.value)}
                                        error={errors?.halls?.[idx]?.[field.name]}
                                      />
                                    ))}
                                    {RoomDetails.map(field => field.name === "include_hall_rent" && (
                                      <Switch
                                        key={`${field.name}-${idx}`}
                                        values={hall.include_hall_rent}
                                        handleChange={(e) => handleHallChange(idx, "include_hall_rent", e.target.value)}
                                        error={errors?.halls?.[idx]?.[field.name]}
                                        field={{ ...field, disabled: !isHallSectionEnabled, name: `${field.name}_${idx}` }}
                                        ValueOne="1" ValueTwo="0" LabelOne="Yes" LabelTwo="No"
                                      />
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="border-start"></td>
                          </tr>

                          <tr className="border-bottom">
                            <td colSpan={2} className="p-0 border-end">
                              <div className="bg-light bg-opacity-10 px-3 py-2 fw-semibold border-bottom border-top">Food Booking Option</div>
                              <div className="row g-3 p-3">
                                {RoomDetails.map(field => {
                                  if (field.name === "food_package_id") {
                                    return (
                                      <FormSelect
                                        key={field.name}
                                        field={{ ...field, disabled: !isHallSectionEnabled }}
                                        value={hall.food_package_id}
                                        options={getSelectOptions(field.name, optionConfig)}
                                        handleChange={(e) => handleHallChange(idx, "food_package_id", e.target.value)}
                                        error={errors?.halls?.[idx]?.food_package_id}
                                      />
                                    );
                                  }
                                  if (field.name === "food_price") {
                                    return (
                                      <FormInput
                                        key={field.name}
                                        field={{ ...field, disabled: !isHallSectionEnabled }}
                                        value={hall.food_price || 0}
                                        handleChange={(e) => handleHallChange(idx, "food_price", e.target.value)}
                                        error={errors?.halls?.[idx]?.food_price}
                                      />
                                    );
                                  }
                                  if (field.name === "food_quantity") {
                                    return (
                                      <FormInput
                                        key={field.name}
                                        field={{ ...field, disabled: !isHallSectionEnabled }}
                                        value={hall.food_quantity || 0}
                                        handleChange={(e) => handleHallChange(idx, "food_quantity", e.target.value)}
                                        error={errors?.halls?.[idx]?.food_quantity}
                                      />
                                    );
                                  }
                                  return null;
                                })}

                                {hall.food_package_id && foodPackageDetails[hall.food_package_id] && (
                                  <div className="col-12 mt-3">
                                    <div className="alert alert-info border-0 bg-info bg-opacity-10 py-2 px-3 small d-flex align-items-center gap-2 mb-3" style={{ borderRadius: "10px" }}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-info"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="16" x2="12" y2="12"></line><line x1="12" y1="8" x2="12.01" y2="8"></line></svg>
                                      <span className="text-info fw-medium">Customize your package menu below. Limits are enforced automatically.</span>
                                    </div>
                                    <div className="row g-3">
                                      {foodPackageDetails[hall.food_package_id]?.food_type_limits?.map((limit, lIdx) => {
                                        const typeEntry = (hall.selected_foods || []).find(sf => String(sf.food_type_id) === String(limit.food_type_id));
                                        const currentSelected = typeEntry?.food_ids || [];
                                        const foodsInType = limit.foods || [];

                                        return (
                                          <div key={lIdx} className="col-md-6">
                                            <div className="card h-100 border shadow-sm food-category-card " style={{ borderRadius: "15px" }}>
                                              <div className="card-header border-bottom-0 bg-light px-4 pt-4 pb-0 d-flex justify-content-between align-items-center food-header-section">
                                                <div>
                                                  <h6 className="mb-1 text-dark fw-bold fs-6">{limit.food_type_name}</h6>
                                                  <small className="text-muted">Choose favorites</small>
                                                </div>
                                                <span className={`selection-badge badge ${currentSelected.length >= limit.allowed_quantity ? "bg-success" : "bg-primary text-white"} rounded-pill px-3 py-2`}>
                                                  {currentSelected.length} / {limit.allowed_quantity}
                                                </span>
                                              </div>
                                              <div className="card-body p-4">
                                                <FormMultiSelect
                                                  field={{ name: `food_type_${limit.food_type_id}`, label: `${limit.food_type_name} (Max: ${limit.allowed_quantity})`, required: false, fieldSize: "d-block" }}
                                                  value={currentSelected}
                                                  handleChange={(e) => {
                                                    const newSelectedFoods = [...(hall.selected_foods || [])];
                                                    let entry = newSelectedFoods.find(sf => String(sf.food_type_id) === String(limit.food_type_id));
                                                    if (!entry) { entry = { food_type_id: limit.food_type_id, food_ids: [] }; newSelectedFoods.push(entry); }
                                                    if (e.target.value.length <= limit.allowed_quantity) { entry.food_ids = e.target.value; handleHallChange(idx, "selected_foods", newSelectedFoods); }
                                                  }}
                                                  options={foodsInType.map(f => ({ value: f.food_id, label: f.food_name }))}
                                                  placeholder={`Select up to ${limit.allowed_quantity} items`}
                                                />

                                                {currentSelected.length > 0 && (
                                                  <div className="selected-items-section mt-3">
                                                    <div className="row g-2">
                                                      {currentSelected.map(foodId => {
                                                        const food = foodsInType.find(f => f.food_id === foodId);
                                                        if (!food) return null;
                                                        return (
                                                          <div key={foodId} className="col-lg-6 col-sm-12">
                                                            <div className="food-item-card selected-food-item">
                                                              <span className="food-name">{food.food_name}</span>
                                                              <button type="button" className="view-icon-btn" onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleImageClick(food); }}>👁</button>
                                                            </div>
                                                          </div>
                                                        );
                                                      })}
                                                    </div>
                                                  </div>
                                                )}
                                              </div>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td className="align-bottom text-center px-2 py-3">
                              {halls.length > 1 && (
                                <button type="button" className="btn btn-sm btn-danger px-1" onClick={() => removeHall(idx)} title="Remove Hall">
                                  <svg xmlns="http://www.w3.org/2000/svg" width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>
                                </button>
                              )}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {imageModal.isOpen && (
        <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{imageModal.foodName}</h5>
                <button type="button" className="btn-close" onClick={closeImageModal}></button>
              </div>
              <div className="modal-body">
                <div className="row">
                  {imageModal.imageUrl && (
                    <div className="col-md-6 text-center">
                      <img src={imageModal.imageUrl} alt={imageModal.foodName} className="img-fluid" />
                    </div>
                  )}
                  <div className={imageModal.imageUrl ? 'col-md-6' : 'col-12'}>
                    <h6>Description</h6>
                    <p>{imageModal.foodDescription || "No description available."}</p>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={closeImageModal}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HallRoomDetailsList;
